/**
 * Pure MedMic Validate — no Enterprise Zustand, no publish, no MedMic store writes.
 */

import { API_BASE_URLS } from "../config/apiBaseUrlsConstants";
import { getSafeTokenMeta, logMedmicAuthDiag } from "./creTokenDiagnostics";

/**
 * @typedef {object} MedMicValidateSuccess
 * @property {true} ok
 * @property {Record<string, unknown>} user
 * @property {string | null} userEmail
 */

/**
 * @typedef {object} MedMicValidateFailure
 * @property {false} ok
 * @property {{ code: string, message: string }} error
 */

/**
 * Validate a candidate access token against CRE TokenValidation.
 *
 * @param {string} accessToken
 * @param {{ source?: string, signal?: AbortSignal }} [opts]
 * @returns {Promise<MedMicValidateSuccess | MedMicValidateFailure>}
 */
export async function validateMedmicToken(accessToken, opts = {}) {
  const source = opts.source ?? "validate";
  const meta = getSafeTokenMeta(accessToken);

  if (!accessToken || typeof accessToken !== "string") {
    return {
      ok: false,
      error: { code: "missing_token", message: "No candidate token to validate." },
    };
  }

  try {
    const response = await fetch(
      `${API_BASE_URLS.reportingCre}/api/TokenValidation/Validate`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${accessToken}` },
        signal: opts.signal,
      },
    );

    logMedmicAuthDiag("medmic", "validate_http", {
      source,
      ok: response.ok,
      status: response.status,
      fingerprint: meta.fingerprint,
      aud: meta.aud,
      exp: meta.exp,
    });

    if (!response.ok) {
      return {
        ok: false,
        error: {
          code: "validate_http_error",
          message: `Validate failed with HTTP ${response.status}.`,
        },
      };
    }

    const data = await response.json();
    if (!data?.success || !data?.user) {
      return {
        ok: false,
        error: {
          code: "validate_payload_invalid",
          message: "Validate response did not include a user.",
        },
      };
    }

    const user = data.user;
    const userEmail =
      (typeof user.email === "string" && user.email) ||
      (typeof user.userEmail === "string" && user.userEmail) ||
      null;

    return { ok: true, user, userEmail };
  } catch (err) {
    if (err?.name === "AbortError") {
      return {
        ok: false,
        error: { code: "validate_aborted", message: "Validate aborted." },
      };
    }
    const message = err instanceof Error ? err.message : String(err);
    logMedmicAuthDiag("medmic", "validate_error", {
      source,
      message,
      fingerprint: meta.fingerprint,
    });
    return {
      ok: false,
      error: { code: "validate_network_error", message },
    };
  }
}
